import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import Modal from "react-modal";
import { displayToast } from "../utils/Toast";
import { createCategory, updateCategory } from "../apis/transaction";
import "../styles/components/MasterDataModal.css";

const CategoryManagementModal = ({
  isOpen,
  onClose,
  onSuccess,
  mode = "add",
  categoryData = null,
}) => {
  const [categoryName, setCategoryName] = useState("");
  const [status, setStatus] = useState("active");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && mode === "edit" && categoryData) {
      setCategoryName(categoryData.category_name || "");
      setStatus(categoryData.status || "active");
    } else if (isOpen && mode === "add") {
      setCategoryName("");
      setStatus("active");
    }
  }, [isOpen, mode, categoryData]);

  const resetForm = () => {
    setCategoryName("");
    setStatus("active");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      displayToast("Please enter a category name", "error");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        category_name: categoryName.trim(),
        status: status,
      };

      let response;
      if (mode === "add") {
        response = await createCategory(payload);
        if (response && (response.id || response.status === "success")) {
          displayToast("Category created successfully", "success");
          resetForm();
          onSuccess();
        } else if (response?.message) {
          displayToast(response.message, "error");
        } else {
          displayToast("Failed to create category", "error");
        }
      } else if (mode === "edit" && categoryData?.id) {
        response = await updateCategory(categoryData.id, payload);
        if (response && (response.status === "success" || response.id)) {
          displayToast("Category updated successfully", "success");
          resetForm();
          onSuccess();
        } else if (response?.message) {
          displayToast(response.message, "error");
        } else {
          displayToast("Failed to update category", "error");
        }
      }
    } catch (error) {
      displayToast("Error saving category: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="master-data-modal-container category-management-modal"
      overlayClassName="modal-overlay compact-modal-overlay"
      contentLabel={mode === "add" ? "Add Category" : "Edit Category"}
    >
      <div className="master-data-modal-header" style={{ backgroundColor: "#8B9BBD" }}>
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {mode === "add" ? "Add Category" : "Edit Category"}
          </h2>
          <X
            className="sales-form-icons cursor-pointer"
            onClick={handleClose}
            size={24}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div className="master-data-modal-form-fields">
          <div className="master-data-modal-field">
            <label>Category Name *</label>
            <input
              type="text"
              className="master-data-modal-input"
              placeholder="Enter category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              required
              maxLength={100}
              autoFocus
            />
          </div>

          <div className="master-data-modal-field">
            <label>Status</label>
            <select
              className="master-data-modal-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="master-data-modal-footer">
          {isLoading ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                className="master-data-modal-cancel-button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="master-data-modal-save-button"
                disabled={isLoading}
              >
                {mode === "add" ? "Create" : "Update"}
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default CategoryManagementModal;
